'use client';

import { lazy, Suspense, useCallback, useMemo } from 'react';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useChartSeries, useThemeColors } from '@/lib/theme/useThemeColors';
import type { CategoryRow } from './buildCategoryRows';

const ReactECharts = lazy(() =>
  import('echarts-for-react').then((m) => ({ default: m.default })),
);

// ECharts renders tooltip strings as HTML, and slice names can carry
// user-provided text (custom category labels).
const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};
const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);

interface Props {
  rows: readonly CategoryRow[];
  total: number;
  selectedId?: string | null;
  onSelect?: (subcategoryId: string) => void;
}

export function SubcategoryDonut({ rows, total, selectedId = null, onSelect }: Props) {
  const theme = useThemeColors();
  const series = useChartSeries();
  const formatCurrency = useFormatCurrency();
  // Stable reference (keyed only on formatCurrency, itself stable unless Privacy
  // Mode flips) so `option` below stays deep-equal across re-renders with
  // unchanged data — see the module comment this replaced for why that matters.
  const tooltipFormatter = useCallback(
    (params: { name: string; value: number; percent: number }) =>
      `${escapeHtml(params.name)}<br/><b>${formatCurrency(params.value)}</b> · ${params.percent.toFixed(1)}%`,
    [formatCurrency],
  );
  const option = useMemo(() => {
    const hasSelection = Boolean(selectedId);
    return {
      tooltip: {
        trigger: 'item',
        formatter: tooltipFormatter,
      },
      series: [
        {
          type: 'pie',
          radius: ['55%', '82%'],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 4, borderColor: theme.surface, borderWidth: 2 },
          label: { show: false },
          labelLine: { show: false },
          data: rows.map((r, i) => ({
            name: r.label,
            value: r.amount,
            itemStyle: {
              color: series[i % series.length],
              opacity: hasSelection && selectedId !== r.id ? 0.35 : 1,
              borderColor: selectedId === r.id ? theme.textPrimary : theme.surface,
              borderWidth: selectedId === r.id ? 3 : 2,
            },
            id: r.id,
          })),
        },
      ],
    };
  }, [rows, selectedId, theme, series, tooltipFormatter]);

  if (rows.length === 0) {
    return (
      <div className="flex h-[220px] items-center justify-center rounded-xl bg-surface-sunken text-sm text-text-tertiary">
        Sem subcategorias detalhadas
      </div>
    );
  }

  return (
    <div className="relative">
      <Suspense
        fallback={<div className="h-[220px] rounded-xl bg-surface-sunken" aria-hidden />}
      >
        <ReactECharts
          option={option}
          style={{ height: 220, width: '100%' }}
          opts={{ renderer: 'svg' }}
          onEvents={
            onSelect
              ? {
                  click: (e: { data?: { id?: string } }) => {
                    if (e?.data?.id) onSelect(e.data.id);
                  },
                }
              : undefined
          }
        />
      </Suspense>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xs tracking-[0.15em] text-text-tertiary uppercase">
          Total
        </span>
        <span className="font-mono text-sm font-semibold text-text-primary tabular-nums">
          {formatCurrency(total)}
        </span>
      </div>
    </div>
  );
}
